import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import aiAutomationApi from '../utils/aiAutomationApi';
import tierConfig from '../config/aiTierFeatures.json';

/**
 * OpenAIKeyWarning — Per-provider quota exceeded banner in the topbar.
 *
 * Fetches per-tier AI health from GET /ai/health.
 * Shows a compact red badge when any tier has quota_exceeded or error.
 * On click, expands to list each FAILED PROVIDER (grouped) with a "Check Again" button.
 *
 * Auto-recovery: polling every 60s + manual "Check Again" per provider.
 */

const POLL_INTERVAL = 120_000; // 2 minutes

const OpenAIKeyWarning = () => {
  const { isDark } = useTheme();
  const location = useLocation();

  const [tierStatuses, setTierStatuses] = useState(null); // { HEAVY: {...}, MID: {...}, ... }
  const [expanded, setExpanded] = useState(false);
  const [checkingProviders, setCheckingProviders] = useState({}); // { "openai": true }
  const [lastChecked, setLastChecked] = useState(null);
  const badgeRef = useRef(null);
  const popoverRef = useRef(null);
  const intervalRef = useRef(null);
  const hasProbed = useRef(false);

  // Fetch per-tier health status
  const fetchHealth = useCallback(async (probe = false) => {
    const token = localStorage.getItem('auth_token');
    if (!token) { setTierStatuses(null); return; }

    try {
      const url = probe ? '/ai/health?probe=true' : '/ai/health';
      const result = await aiAutomationApi.get(url);
      if (result?.tiers) {
        setTierStatuses(result.tiers);
        setLastChecked(new Date());
      }
    } catch (error) {
      console.warn('[OpenAIKeyWarning] Health fetch failed:', error?.message);
    }
  }, []);

  // Poll on mount + interval. First fetch uses probe=true to warm up after restart.
  useEffect(() => {
    fetchHealth(!hasProbed.current);
    hasProbed.current = true;
    intervalRef.current = setInterval(() => fetchHealth(false), POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-check on route change (debounced)
  useEffect(() => {
    if (lastChecked && (Date.now() - lastChecked.getTime()) < POLL_INTERVAL) return;
    fetchHealth();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time update: listen for piggyback health data from chat responses
  useEffect(() => {
    const handler = (e) => {
      if (e.detail && typeof e.detail === 'object') {
        setTierStatuses(e.detail);
        setLastChecked(new Date());
      }
    };
    window.addEventListener('aiHealthUpdate', handler);
    return () => window.removeEventListener('aiHealthUpdate', handler);
  }, []);

  // Close popover on outside click
  useEffect(() => {
    if (!expanded) return;
    const handleClick = (e) => {
      const clickedBadge = badgeRef.current?.contains(e.target);
      const clickedPopover = popoverRef.current?.contains(e.target);
      if (!clickedBadge && !clickedPopover) setExpanded(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [expanded]);

  // Don't render on public pages
  const publicPrefixes = ['/auth/', '/interviews/book/', '/interview/take/', '/ai-interview/launch/', '/reschedule-form/', '/calendar-callback', '/gmail-callback', '/outlook-callback', '/change-mail'];
  if (publicPrefixes.some(p => location.pathname.startsWith(p))) return null;

  // Derive failed providers grouped by name
  if (!tierStatuses) return null;

  const ERROR_TYPE_LABELS = {
    invalid_key: 'Invalid API Key',
    quota_exceeded: 'Quota Exceeded',
    rate_limited: 'Rate Limited',
    provider_down: 'Provider Down',
    model_not_found: 'Model Not Found',
  };

  const failedProviders = {}; // { "openai": { tiers: ["HEAVY", "LIGHT"], status: "...", error_type: "..." } }
  let hasAnyFailure = false;

  for (const [tier, data] of Object.entries(tierStatuses)) {
    if (data.status === 'quota_exceeded' || data.status === 'error') {
      hasAnyFailure = true;
      const prov = data.provider || 'unknown';
      if (!failedProviders[prov]) {
        failedProviders[prov] = { tiers: [], status: data.status, error_type: data.error_type };
      }
      failedProviders[prov].tiers.push(tier);
      // Use the most specific error_type
      if (data.error_type && !failedProviders[prov].error_type) {
        failedProviders[prov].error_type = data.error_type;
      }
    }
  }

  // Auto-close when all healthy
  if (!hasAnyFailure) {
    if (expanded) setExpanded(false);
    return null;
  }

  const failedCount = Object.keys(failedProviders).length;
  const _firstProv = Object.keys(failedProviders)[0] || '';
  const firstProvider = _firstProv.charAt(0).toUpperCase() + _firstProv.slice(1);
  const firstErrorType = Object.values(failedProviders)[0]?.error_type;
  const errorLabel = ERROR_TYPE_LABELS[firstErrorType] || (Object.values(failedProviders).every(p => p.status === 'quota_exceeded') ? 'Quota Exceeded' : 'API Issue');
  const label = failedCount === 1
    ? `${firstProvider} ${errorLabel}`
    : `AI ${errorLabel}`;

  // "Check Again" for a specific provider — tests all its failed tiers
  const handleCheckProvider = async (providerName) => {
    setCheckingProviders(prev => ({ ...prev, [providerName]: true }));
    try {
      const tiers = failedProviders[providerName]?.tiers || [];
      for (const tier of tiers) {
        try {
          await aiAutomationApi.post('/ai/test-tier', { tier });
        } catch { /* ignore individual failures */ }
      }
      // Re-fetch health to get updated statuses
      await fetchHealth();
    } finally {
      setCheckingProviders(prev => ({ ...prev, [providerName]: false }));
    }
  };

  const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

  return (
    <div className="relative" ref={badgeRef}>
      {/* Compact badge */}
      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors"
        style={{
          backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#FEF2F2',
          color: isDark ? '#FCA5A5' : '#DC2626',
          border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.3)' : '#FECACA'}`,
        }}
      >
        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span className="hidden sm:inline">{label}</span>
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
            style={{ backgroundColor: isDark ? '#FCA5A5' : '#EF4444' }} />
          <span className="relative inline-flex rounded-full h-2 w-2"
            style={{ backgroundColor: isDark ? '#FCA5A5' : '#EF4444' }} />
        </span>
      </button>

      {/* Expanded popover — grouped by provider */}
      {expanded && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            top: badgeRef.current ? badgeRef.current.getBoundingClientRect().bottom + 8 : 60,
            left: badgeRef.current ? badgeRef.current.getBoundingClientRect().left : 100,
            zIndex: 9999,
            width: '360px',
          }}
        >
          <div style={{
            borderRadius: '12px',
            border: `1px solid ${isDark ? '#4B5563' : '#E5E7EB'}`,
            backgroundColor: isDark ? '#111827' : '#FFFFFF',
            boxShadow: isDark
              ? '0 10px 25px rgba(0,0,0,0.5), 0 4px 10px rgba(0,0,0,0.3)'
              : '0 10px 25px rgba(0,0,0,0.15), 0 4px 10px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${isDark ? '#374151' : '#F3F4F6'}`,
              backgroundColor: isDark ? '#1F2937' : '#F9FAFB',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <svg style={{ width: '16px', height: '16px', color: isDark ? '#FCA5A5' : '#EF4444', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span style={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#F9FAFB' : '#111827' }}>
                AI Service Issues
              </span>
              {lastChecked && (
                <span style={{ fontSize: '11px', color: isDark ? '#9CA3AF' : '#6B7280', marginLeft: 'auto' }}>
                  {lastChecked.toLocaleTimeString()}
                </span>
              )}
            </div>

            {/* Provider list */}
            <div style={{ padding: '8px' }}>
              {Object.entries(failedProviders).map(([providerName, info]) => {
                const isChecking = checkingProviders[providerName];
                const isQuota = info.status === 'quota_exceeded';
                const tierLabels = info.tiers.map(t => tierConfig[t]?.label || t).join(', ');

                return (
                  <div
                    key={providerName}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 12px', borderRadius: '8px',
                      backgroundColor: isDark ? 'rgba(239, 68, 68, 0.08)' : '#FEF2F2',
                      marginBottom: '4px',
                    }}
                  >
                    {/* Red dot */}
                    <span style={{
                      width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                      backgroundColor: '#EF4444',
                    }} />

                    {/* Provider info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#F9FAFB' : '#111827' }}>
                        {capitalize(providerName)}
                      </div>
                      <div style={{ fontSize: '11px', color: isDark ? '#9CA3AF' : '#6B7280', marginTop: '2px' }}>
                        {isQuota ? 'Quota exceeded' : 'API error'} &middot; {tierLabels} tier{info.tiers.length > 1 ? 's' : ''}
                      </div>
                    </div>

                    {/* Check Again button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCheckProvider(providerName); }}
                      disabled={isChecking}
                      style={{
                        padding: '5px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                        cursor: isChecking ? 'not-allowed' : 'pointer',
                        opacity: isChecking ? 0.6 : 1,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#FFFFFF',
                        color: isDark ? '#D1D5DB' : '#374151',
                        border: `1px solid ${isDark ? '#4B5563' : '#D1D5DB'}`,
                        whiteSpace: 'nowrap', flexShrink: 0,
                        transition: 'background-color 0.15s',
                      }}
                    >
                      {isChecking ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <svg style={{ width: '12px', height: '12px' }} className="animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Checking
                        </span>
                      ) : 'Check Again'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default OpenAIKeyWarning;
